import React from "react";
import { LicenseCreation } from "./update-tool.types";
import styles from "./create-licenses.section.module.scss";
import { DropdownOption } from "../../../../../common/types/app.types";
import DropdownSelector from "../../../../Common/dropdown-selector";
import PriceInput from "../../../../Common/price-input";
import NumberInputWithControls from "../../../../Common/number-input-with-controls/number-input-with-controls.component";

interface DropdownItem {
  label: string;
  value: string;
}

interface CreateLicensesSectionProps {
  monitoringOptions: DropdownOption[];
  licenseCreation: LicenseCreation;
  monitoringOption: string;
  setFieldValue: (field: string, value: any) => void;
  isView?: boolean;
}

const CreateLicensesSection = ({ monitoringOptions, licenseCreation, monitoringOption, setFieldValue, isView = false }: CreateLicensesSectionProps) => {
  const knowPrice = { label: licenseCreation.knowPrice, value: licenseCreation.knowPrice };
  const currency = { label: licenseCreation.currency, value: licenseCreation.currency };
  const price = licenseCreation.price;
  const priceUnit = { label: licenseCreation.priceUnit, value: licenseCreation.priceUnit };
  const priceInterval = { label: licenseCreation.priceInterval, value: licenseCreation.priceInterval };
  const licensesQty = licenseCreation.licensesQty;
  const socMonitoring = monitoringOptions.find(opt => opt.value === monitoringOption) || monitoringOptions[0] || { label: "Always", value: "Always" };

  const getCurrencySymbol = (currencyValue: string): string => {
    const symbols: Record<string, string> = {
      GBP: "£",
      USD: "$",
      EUR: "€",
    };
    return symbols[currencyValue] || "£";
  };

  const knowPriceOptions: DropdownItem[] = [
    { label: "Yes", value: "Yes" },
    { label: "No", value: "No" },
  ];

  const currencyOptions: DropdownItem[] = [
    { label: "GBP", value: "GBP" },
    { label: "USD", value: "USD" },
    { label: "EUR", value: "EUR" },
  ];

  const priceUnitOptions: DropdownItem[] = [
    { label: "/user", value: "/user" },
    { label: "/team", value: "/team" },
    { label: "/company", value: "/company" },
  ];

  const priceIntervalOptions: DropdownItem[] = [
    { label: "/month", value: "/month" },
    { label: "/year", value: "/year" },
    { label: "/quarter", value: "/quarter" },
  ];

  const handleIncrementLicenses = () => {
    if (isView) return;
    setFieldValue('licenseCreation.licensesQty', Math.max(0, licensesQty + 1));
  };

  const handleDecrementLicenses = () => {
    if (isView) return;
    setFieldValue('licenseCreation.licensesQty', Math.max(0, licensesQty - 1));
  };

  const handleLicensesQtyChange = (value: string | number) => {
    if (isView) return;
    const numValue = typeof value === "string" ? Number.parseInt(value) || 0 : value;
    setFieldValue('licenseCreation.licensesQty', Math.max(0, numValue));
  };

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <p className={styles.question}>
          Do you know how much you pay for this tool?
        </p>

        <div className={styles.selectWrapper}>
          <div className={styles.selectContainer}>
            <DropdownSelector
              list={knowPriceOptions}
              selected={knowPrice}
              onItemSelected={(item) => {
                if (isView) return;
                setFieldValue('licenseCreation.knowPrice', item.value);
              }}
              toggleClassName={styles.selectLabel}
            >
              <div className={styles.select}>
                <span>{knowPrice.label || 'Select'}</span>
                <div className={styles.selectIconWrapper}>
                  <div className={styles.selectIcon}>
                    <div className="arrow-icon" />
                  </div>
                </div>
              </div>
            </DropdownSelector>
          </div>
        </div>
      </div>

      {licenseCreation.knowPrice === 'Yes' && (
        <div className={styles.priceSection}>
          <div className={styles.priceRow}>
            <div className={styles.priceLabelWrapper}>
              <label
                htmlFor="price-input"
                className={styles.priceLabel}
              >
                Price
              </label>
            </div>

            <div className={styles.priceInputs}>
              <DropdownSelector
                list={currencyOptions}
                selected={currency}
                onItemSelected={(item) => {
                  if (isView) return;
                  setFieldValue('licenseCreation.currency', item.value);
                }}
                toggleClassName={styles.currencySelect}
              >
                <div className={styles.currencySelectField}>
                  <span className="f-grey">{currency.label}</span>
                  <div className={styles.currencySelectIcon}>
                    <div className="arrow-icon" />
                  </div>
                </div>
              </DropdownSelector>

              <div className={styles.priceInputWrapper}>
                <PriceInput
                  id="price-input"
                  value={price}
                  onChange={(value) => {
                    if (isView) return;
                    setFieldValue('licenseCreation.price', value);
                  }}
                  prefix={getCurrencySymbol(currency.value)}
                  className={styles.priceInput}
                />
              </div>

              <DropdownSelector
                list={priceUnitOptions}
                selected={priceUnit}
                onItemSelected={(item) => {
                  if (isView) return;
                  setFieldValue('licenseCreation.priceUnit', item.value);
                }}
                toggleClassName={styles.unitSelect}
              >
                <div className={styles.unitSelectField}>
                  <span className="f-grey">{priceUnit.label}</span>
                  <div className={styles.unitSelectIcon}>
                    <div className="arrow-icon" />
                  </div>
                </div>
              </DropdownSelector>

              <DropdownSelector
                list={priceIntervalOptions}
                selected={priceInterval}
                onItemSelected={(item) => {
                  if (isView) return;
                  setFieldValue('licenseCreation.priceInterval', item.value);
                }}
                toggleClassName={styles.intervalSelect}
              >
                <div className={styles.intervalSelectField}>
                  <span className="f-grey">{priceInterval.label}</span>
                  <div className={styles.intervalSelectIcon}>
                    <div className="arrow-icon" />
                  </div>
                </div>
              </DropdownSelector>
            </div>
          </div>

          <div className={styles.licensesRow}>
            <div className={styles.licensesLabelWrapper}>
              <label
                htmlFor="licenses-qty"
                className={styles.licensesLabel}
              >
                Licenses Qty
              </label>
            </div>

            <div className={styles.licensesInputWrapper}>
              <NumberInputWithControls
                id="licenses-qty"
                value={licensesQty}
                onChange={handleLicensesQtyChange}
                onIncrement={handleIncrementLicenses}
                onDecrement={handleDecrementLicenses}
                min="0"
                inputType="number"
                ariaLabel="Licenses quantity"
                wrapperClassName={styles.licensesInputContainer}
              />
            </div>
          </div>
        </div>
      )}

      <div className={styles.socSection}>
        <p className={styles.socQuestion}>
          Is this monitored by a SOC?
        </p>

        <div className={styles.selectWrapper}>
          <div className={styles.selectContainer}>
            <DropdownSelector
              list={monitoringOptions}
              selected={socMonitoring}
              onItemSelected={(item) => setFieldValue('monitoringOption', item.value)}
              toggleClassName={styles.selectLabel}
            >
              <div className={styles.select}>
                <span>{socMonitoring.label}</span>
                <div className={styles.selectIconWrapper}>
                  <div className={styles.selectIcon}>
                    <div className="arrow-icon" />
                  </div>
                </div>
              </div>
            </DropdownSelector>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CreateLicensesSection;
